<template>
  <el-dialog
    :title="`指定标签分配到公司 — ${tagName}`"
    :visible.sync="visible"
    width="80%"
    :close-on-click-modal="false"
    top="5vh"
    @closed="onClosed"
  >
    <div class="assign-layout">
      <div class="assign-layout__left">
        <h4 class="assign-title"><i class="el-icon-tag"></i> 选择标签（可多选）</h4>
        <el-input
          v-model="tagSearch"
          placeholder="搜索标签名称/code..."
          prefix-icon="el-icon-search"
          size="medium"
          clearable
          style="margin-bottom:12px"
        ></el-input>
        <div class="assign-list custom-scrollbar">
          <div
            v-for="tag in filteredTags"
            :key="tag.detailId"
            class="assign-item"
            :class="{ 'assign-item--selected': selectedTags.includes(tag.detailId) }"
            @click="toggleTag(tag.detailId)"
          >
            <el-checkbox :value="selectedTags.includes(tag.detailId)" class="assign-item__check"></el-checkbox>
            <div class="assign-item__info">
              <span class="assign-item__name">{{ tag.name }}</span>
              <span class="assign-item__code">{{ tag.code }}</span>
              <span class="assign-item__desc">{{ tag.desc || '' }}</span>
            </div>
          </div>
        </div>
        <div class="assign-count">已选 <b>{{ selectedTags.length }}</b> 个标签</div>
      </div>
      <div class="assign-layout__right">
        <div class="assign-right-header">
          <h4 class="assign-title"><i class="el-icon-office-building"></i> 选择目标公司</h4>
          <el-input
            v-model="companySearch"
            placeholder="搜索公司名称..."
            prefix-icon="el-icon-search"
            size="mini"
            clearable
            class="assign-right-header__search"
          ></el-input>
          <el-button
            size="mini"
            :type="allCompaniesSelected ? 'primary' : ''"
            plain
            :disabled="filteredCompanies.length === 0"
            @click="toggleSelectAll"
          >{{ allCompaniesSelected ? '取消全选' : '全选' }}</el-button>
        </div>
        <div class="assign-filter-btns">
          <el-button
            :type="companyFilter === 'all' ? 'primary' : ''"
            size="small"
            @click="companyFilter = 'all'"
          >全部</el-button>
          <el-button
            v-for="opt in companyTypeOptions"
            :key="opt.value"
            :type="companyFilter === opt.value ? 'primary' : ''"
            size="small"
            @click="companyFilter = opt.value"
          >{{ opt.label }}</el-button>
        </div>
        <div class="assign-list custom-scrollbar">
          <div
            v-for="co in filteredCompanies"
            :key="co.id"
            class="assign-item"
            :class="{ 'assign-item--selected': selectedCompanies.includes(co.id) }"
            @click="toggleCompany(co.id)"
          >
            <el-checkbox :value="selectedCompanies.includes(co.id)" class="assign-item__check"></el-checkbox>
            <div class="assign-item__info">
              <span class="assign-item__name">{{ co.companyName }}</span>
            </div>
            <el-tag :type="companyTagType(co.businessType)" size="mini">{{ co.type }}</el-tag>
          </div>
        </div>
        <div class="assign-count">已选 <b>{{ selectedCompanies.length }}</b> 家公司</div>
      </div>
    </div>
    <div slot="footer" class="assign-footer">
      <span class="assign-summary">
        已选 <b>{{ selectedTags.length }}</b> 个标签 → 已选 <b>{{ selectedCompanies.length }}</b> 家公司
      </span>
      <div>
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="confirmAssign">确认分配</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { BUSINESS_TYPE_OPTIONS } from '../../../utils/business-type';

export default {
  name: 'AssignCompanyDialog',
  props: {
    generalTags: { type: Array, default: () => [] },
    // 目标公司列表，由父级透传公司池接口 (/company/getCompanyListJudgeRole) 的数据
    companies: { type: Array, default: () => [] }
  },
  data() {
    return {
      visible: false,
      tagName: '',
      selectedTags: [],
      selectedCompanies: [],
      tagSearch: '',
      companySearch: '',
      companyFilter: 'all',
      // 分配请求进行中
      submitting: false
    };
  },
  computed: {
    filteredTags() {
      if (!this.tagSearch) return this.generalTags;
      const kw = this.tagSearch.toLowerCase();
      return this.generalTags.filter(t =>
        `${t.name}${t.code}${t.desc || ''}`.toLowerCase().includes(kw)
      );
    },
    // 筛选按钮只展示当前公司列表中真实存在的业务类型，避免出现筛不出数据的空按钮；
    // businessType 可能是枚举值，也可能是中文文案，两种都要能匹配上，否则会漏掉按钮
    companyTypeOptions() {
      const used = [];
      this.companies.forEach((c) => {
        const value = this.matchBusinessType(c.businessType);
        if (value && used.indexOf(value) === -1) used.push(value);
      });
      return BUSINESS_TYPE_OPTIONS.filter(opt => used.indexOf(opt.value) > -1);
    },
    // 业务类型筛选与公司名搜索叠加：两者都只影响列表展示，
    // 全选/取消全选同样只作用于这里的筛选结果
    filteredCompanies() {
      let list = this.companies;
      if (this.companyFilter !== 'all') {
        list = list.filter(c => this.matchBusinessType(c.businessType) === this.companyFilter);
      }
      if (this.companySearch) {
        const kw = this.companySearch.trim().toLowerCase();
        list = list.filter(c => (c.companyName || '').toLowerCase().includes(kw));
      }
      return list;
    },
    // 当前筛选结果是否已全部选中（结果为空时不算全选）
    allCompaniesSelected() {
      const list = this.filteredCompanies;
      if (!list.length) return false;
      return list.every(c => this.selectedCompanies.indexOf(c.id) > -1);
    }
  },
  methods: {
    // detailId: 标签明细 id，作为勾选与提交的唯一标识
    open(detailId) {
      const tag = this.generalTags.find(t => t.detailId === detailId);
      this.tagName = tag ? tag.name : '标签名称';
      this.selectedTags = detailId == null ? [] : [detailId];
      this.selectedCompanies = [];
      this.tagSearch = '';
      this.companySearch = '';
      this.companyFilter = 'all';
      this.submitting = false;
      this.visible = true;
    },
    toggleTag(id) {
      const idx = this.selectedTags.indexOf(id);
      if (idx > -1) {
        this.selectedTags.splice(idx, 1);
      } else {
        this.selectedTags.push(id);
      }
    },
    // 公司业务类型归一化为 BUSINESS_TYPE_OPTIONS 的 value：
    // 接口可能返回枚举值（insurance）或中文文案（保险类），两者都能匹配，未知值原样返回
    matchBusinessType(businessType) {
      if (businessType === undefined || businessType === null || businessType === '') return '';
      const option = BUSINESS_TYPE_OPTIONS.find(
        o => o.value === businessType || o.label === businessType
      );
      return option ? option.value : businessType;
    },
    // 业务类型枚举 -> el-tag 主题色
    companyTagType(businessType) {
      if (businessType === 'collection') return '';
      if (businessType === 'insurance') return 'danger';
      if (businessType === 'marketing') return 'warning';
      return 'info';
    },
    toggleCompany(id) {
      const idx = this.selectedCompanies.indexOf(id);
      if (idx > -1) {
        this.selectedCompanies.splice(idx, 1);
      } else {
        this.selectedCompanies.push(id);
      }
    },
    // 全选/取消全选：只作用于当前筛选结果，避免误选被筛掉的公司
    toggleSelectAll() {
      const ids = this.filteredCompanies.map(c => c.id);
      if (!ids.length) return;
      if (this.allCompaniesSelected) {
        this.selectedCompanies = this.selectedCompanies.filter(id => ids.indexOf(id) === -1);
        return;
      }
      const next = this.selectedCompanies.slice();
      ids.forEach((id) => {
        if (next.indexOf(id) === -1) next.push(id);
      });
      this.selectedCompanies = next;
    },
    // 确认分配：把选中标签指派给选中公司（公司级，groupId / sourceCompanyId 固定传 0）
    confirmAssign() {
      if (!this.selectedTags.length || !this.selectedCompanies.length) {
        this.$message.warning('请至少选择一个标签和一家公司');
        return;
      }
      if (this.submitting) return;
      this.submitting = true;
      this.$ajax({
        url: '/label/assignedLabelToCompany',
        method: 'POST',
        data: {
          // 来源标签明细 id 集合
          detailIds: this.selectedTags.slice(),
          // 目标公司 id 集合
          companyIds: this.selectedCompanies.slice(),
          // 指派到公司级，策略组固定为 0
          groupId: 0,
          // 通用池标签无来源公司
          sourceCompanyId: 0
        }
      })
        .then(() => {
          this.$message.success(`已成功将 ${this.selectedTags.length} 个标签分配到 ${this.selectedCompanies.length} 家公司`);
          this.visible = false;
          // 通知父级刷新通用标签列表
          this.$emit('assigned');
        })
        .catch(() => {
          this.$message.error('分配失败');
        })
        .then(() => {
          this.submitting = false;
        });
    },
    onClosed() {
      this.$emit('closed');
    }
  }
};
</script>

<style lang="less" scoped>
@import '../../../assets/style/variable.less';

.assign-layout {
  display: flex;
  height: 500px;
  overflow: hidden;

  &__left {
    width: 45%;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #e8e8e8;
    padding-right: 20px;
  }

  &__right {
    width: 55%;
    display: flex;
    flex-direction: column;
    padding-left: 20px;
    background: #fafafa;
  }
}

.assign-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  margin: 0 0 12px;
}

// 标题行：左侧标题 + 右侧全选按钮
.assign-right-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  .assign-title {
    margin: 0;
    white-space: nowrap;
  }

  &__search {
    flex: 1;
    min-width: 0;
    margin: 0 12px;
  }
}

.assign-list {
  flex: 1;
  overflow-y: auto;
}

.assign-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: @primary;
  }

  &--selected {
    border-color: @primary;
    background: #ecf5ff;
  }

  &__check { pointer-events: none; }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__name {
    font-weight: bold;
    color: #303133;
  }

  &__code {
    font-size: 12px;
    color: #c0c4cc;
    font-family: monospace;
  }

  &__desc {
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.assign-count {
  font-size: 13px;
  color: #909399;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  margin-top: 8px;
}

.assign-filter-btns {
  display: flex;
  // 业务类型较多时需要换行，否则按钮被挤压导致文案显示不全
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;

  // Element 相邻按钮自带 margin-left，与 gap 叠加会使间距不均，这里统一去掉
  ::v-deep .el-button {
    margin-left: 0;
    white-space: nowrap;
  }
}

.assign-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.assign-summary {
  font-size: 13px;
  color: #909399;
}

.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #e8e8e8; border-radius: 10px; }
</style>




// WEBPACK FOOTER //
// src/page/main/tagpools/assign-company-dialog.vue